/**
 * TS port of the two server helpers that back a text field's `{{Field:plain}}`
 * / `{{Field:200}}` arguments:
 *
 *  - `extractText`     <- `HtmlText.ExtractText`   (Builder/Html/HtmlText.cs)
 *  - `truncateAtWord`  <- `string.TruncateAtWord`  (Toolkit/Extensions.cs)
 *
 * The client evaluator has to agree with the C# one character for character,
 * or the designer canvas and the published catalog/PDF disagree — the exact
 * defect this port exists to close. So this is a port, not an approximation:
 *
 *  - Text is pulled from a real element tree, not a tag-stripping regex, so a
 *    space lands between two adjacent BLOCK elements ("...end of one." +
 *    "Start of two") but never between inline ones ("<b>x</b>y" -> "xy").
 *  - Entities are NOT decoded and whitespace is NOT collapsed. HtmlAgilityPack's
 *    `InnerText` leaves both alone, and the result goes straight back into HTML.
 *  - The tree is built the way HtmlAgilityPack builds it, which is NOT the way a
 *    browser's DOMParser does — an orphan `<td>` keeps its element (no foster
 *    parenting), `<br>` is void so text either side of it shares one block, and
 *    a `<` that starts no tag stays literal text. That is why this hand-rolls a
 *    tokenizer instead of reaching for `DOMParser`.
 *
 * Verified against HtmlAgilityPack 1.12.4 (the server's pinned version) — see
 * `html-text.parity.test.ts`, whose expectations are real C# output.
 */

/** `HtmlText.BlockTags` — the tags a text node gets separated across. */
const BLOCK_TAGS = new Set([
  "p", "div", "br", "li", "ul", "ol", "tr", "td", "th", "table",
  "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "section", "article",
]);

/** HtmlAgilityPack `ElementsFlags` Empty entries — never a parent, so they must
 *  not go on the open-element stack. */
const VOID_TAGS = new Set([
  "area", "base", "basefont", "bgsound", "br", "col", "embed", "frame", "hr",
  "img", "input", "isindex", "keygen", "link", "meta", "param", "source",
  "spacer", "wbr",
]);

/** HtmlAgilityPack `ElementsFlags` CData entries — their body is one raw text
 *  node, not markup (and it does end up in the extracted text). */
const CDATA_TAGS = new Set(["script", "style", "textarea", "title", "noxhtml"]);

/** C# `Char.IsWhiteSpace`, exactly. JS `\s` differs — it omits U+0085 and adds
 *  U+FEFF — and this set decides where the boundary space is suppressed. */
const WS = "\\t\\n\\v\\f\\r \\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000";
const WS_CHAR = new RegExp("[" + WS + "]");
const WS_ONLY = new RegExp("^[" + WS + "]*$");

/** A `<`/`</` that actually opens a tag. Anything else stays literal text. */
const TAG_START = /<(\/?)([a-zA-Z][^\s/>]*)/y;

/**
 * Strip a field's own markup down to its text, matching `HtmlText.ExtractText`.
 */
export const extractText = (html: string): string => {
  if (!html) return "";

  const n = html.length;
  const lower = html.toLowerCase();

  // Open elements, innermost last. `block` is a unique id for block-level tags
  // and 0 otherwise — it stands in for the C# node reference the server
  // compares with ReferenceEquals.
  const open: { name: string; block: number }[] = [];
  let nextBlock = 1;
  let lastBlock = 0;
  let result = "";
  let i = 0;

  const currentBlock = () => {
    for (let k = open.length - 1; k >= 0; k--) {
      if (open[k].block) return open[k].block;
    }
    return 0;
  };

  const appendText = (text: string) => {
    if (!text || WS_ONLY.test(text)) return;
    const block = currentBlock();
    if (
      result.length > 0 &&
      block !== lastBlock &&
      !WS_CHAR.test(result[result.length - 1]) &&
      !WS_CHAR.test(text[0])
    ) {
      result += " ";
    }
    lastBlock = block;
    result += text;
  };

  /** Index of the `>` closing the tag that opens at `start`, quotes respected. */
  const findTagEnd = (start: number) => {
    let quote = "";
    for (let k = start + 1; k < n; k++) {
      const c = html[k];
      if (quote) {
        if (c === quote) quote = "";
      } else if (c === '"' || c === "'") {
        quote = c;
      } else if (c === ">") {
        return k;
      }
    }
    return -1;
  };

  while (i < n) {
    const lt = html.indexOf("<", i);
    if (lt < 0) {
      appendText(html.substring(i));
      break;
    }

    // Comments, doctypes and processing instructions carry no text.
    if (html.startsWith("<!--", lt)) {
      appendText(html.substring(i, lt));
      const end = html.indexOf("-->", lt + 4);
      i = end < 0 ? n : end + 3;
      continue;
    }
    if (html.startsWith("<!", lt) || html.startsWith("<?", lt)) {
      appendText(html.substring(i, lt));
      const end = html.indexOf(">", lt);
      i = end < 0 ? n : end + 1;
      continue;
    }

    TAG_START.lastIndex = lt;
    const tag = TAG_START.exec(html);
    if (!tag) {
      // Not a tag — "Size < 10" keeps its angle bracket.
      appendText(html.substring(i, lt + 1));
      i = lt + 1;
      continue;
    }

    appendText(html.substring(i, lt));

    const name = tag[2].toLowerCase();
    const end = findTagEnd(lt);
    i = end < 0 ? n : end + 1;

    if (tag[1] === "/") {
      // Unwind to the matching open element; an unmatched close tag is ignored.
      for (let k = open.length - 1; k >= 0; k--) {
        if (open[k].name === name) {
          open.length = k;
          break;
        }
      }
      continue;
    }

    if (VOID_TAGS.has(name) || (end > lt && html[end - 1] === "/")) continue;

    if (CDATA_TAGS.has(name)) {
      const close = lower.indexOf("</" + name, i);
      appendText(html.substring(i, close < 0 ? n : close));
      if (close < 0) {
        i = n;
      } else {
        const closeEnd = findTagEnd(close);
        i = closeEnd < 0 ? n : closeEnd + 1;
      }
      continue;
    }

    open.push({ name, block: BLOCK_TAGS.has(name) ? nextBlock++ : 0 });
  }

  return result;
};

/**
 * Cut `text` at the first space at or after `min`, matching the server's
 * `TruncateAtWord(min)` (its `max`/`trailing` defaults, which are all the
 * text-tag path ever uses).
 */
export const truncateAtWord = (text: string, min: number, trailing = "...") => {
  if (!text || min <= 0 || text.length <= min) return text;
  const i = text.indexOf(" ", min);
  return (i > 0 ? text.substring(0, i) : text.substring(0, min)) + trailing;
};
